import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, LightbulbFilament } from '@phosphor-icons/react';
import '../styles/App.css';

const HandIcon = ({ size = 40 }) => (
  <div
    role="img"
    aria-label="Handy Co Logo"
    style={{
      width: size,
      height: size,
      backgroundColor: 'var(--crm-text, #9CACA6)',
      WebkitMaskImage: 'url(/assets/icons/icon_handyco.svg)',
      WebkitMaskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskImage: 'url(/assets/icons/icon_handyco.svg)',
      maskRepeat: 'no-repeat',
      maskPosition: 'center',
      maskSize: 'contain',
      flexShrink: 0,
    }}
  />
);

const priorityOptions = [
  { value: 'critical', label: 'Critical' },
  { value: 'important', label: 'Important' },
  { value: 'casual', label: 'Casual' }
];

const getPriorityLabel = (value) => {
  const match = priorityOptions.find(opt => opt.value === (value || '').toLowerCase());
  return match ? match.label : 'Casual';
};

const formatThoughtDate = (value) => {
  if (!value) return '';
  let parsed;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    parsed = new Date(y, m - 1, d);
  } else {
    parsed = new Date(value);
  }
  if (Number.isNaN(parsed.getTime())) return value;
  const now = new Date();
  const sameYear = parsed.getFullYear() === now.getFullYear();
  return parsed.toLocaleDateString([], { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' });
};

const getThoughtSize = (text) => {
  if (!text) return 'small';
  const len = text.length;
  if (len < 50) return 'small';
  if (len < 100) return 'wide';
  if (len < 180) return 'tall';
  return 'large';
};

/**
 * Thoughts page — aggregated view of all thoughts across clients
 */
const Thoughts = () => {
  const navigate = useNavigate();

  const [sessionToken, setSessionToken] = useState('');
  const [user, setUser] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const [thoughts, setThoughts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');

  const [theme, setTheme] = useState(() => localStorage.getItem('crm_theme') || 'dark');

  const authHeaders = useMemo(
    () => (sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {}),
    [sessionToken]
  );

  useEffect(() => {
    const checkSession = async () => {
      const storedToken = localStorage.getItem('crm_session');
      const storedUser = localStorage.getItem('crm_user');

      if (!storedToken) { setAuthLoading(false); navigate('/dashboard'); return; }

      try {
        const res = await fetch('/api/crm/auth/session', {
          headers: { 'Authorization': `Bearer ${storedToken}` }
        });
        const data = await res.json();

        if (res.ok && data.valid) {
          setSessionToken(storedToken);
          setUser(storedUser ? JSON.parse(storedUser) : data.user);
          setAuthenticated(true);
        } else {
          localStorage.removeItem('crm_session');
          localStorage.removeItem('crm_user');
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('Session check error:', err);
        navigate('/dashboard');
      } finally {
        setAuthLoading(false);
      }
    };
    checkSession();
  }, [navigate]);

  const loadThoughts = useCallback(async () => {
    if (!sessionToken) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/crm/thoughts?limit=100', { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to load thoughts');
      setThoughts(data.thoughts || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, authHeaders]);

  useEffect(() => {
    if (authenticated) loadThoughts();
  }, [authenticated, loadThoughts]);

  const filteredThoughts = useMemo(() => {
    if (filterPriority === 'all') return thoughts;
    return thoughts.filter(t => t.priority === filterPriority);
  }, [thoughts, filterPriority]);

  const handleSignOut = () => {
    localStorage.removeItem('crm_session');
    localStorage.removeItem('crm_user');
    navigate('/dashboard');
  };

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('crm_theme', next);
  };

  if (authLoading) {
    return (
      <div className={`crm-page ${theme === 'light' ? 'crm-light' : ''}`}>
        <div className="crm-login-wrapper">
          <div className="crm-login">
            <HandIcon size={80} />
            <div className="crm-auth-loading"><div className="crm-spinner" /><p>Loading...</p></div>
          </div>
        </div>
      </div>
    );
  }
  if (!authenticated) return null;

  return (
    <div className={`crm-page ${theme === 'light' ? 'crm-light' : ''}`}>
      <header className="crm-header">
        <div className="crm-header-brand" onClick={() => navigate('/dashboard')}>
          <HandIcon size={36} />
          <span className="crm-header-title"><span className="crm-header-wordmark">HANDY CO</span></span>
        </div>
        <nav className="crm-header-nav">
          <button className="crm-nav-link" onClick={() => navigate('/dashboard')}>Overview</button>
          <button className="crm-nav-link" onClick={() => navigate('/dashboard/all')}>Clients</button>
          <button className="crm-nav-link" onClick={() => navigate('/dashboard/tasks')}>Tasks</button>
          <button className="crm-nav-link active" onClick={() => navigate('/dashboard/thoughts')}>Thoughts</button>
          <button className="crm-nav-link" onClick={() => navigate('/ds')}>Design Systems</button>
        </nav>
        <div className="crm-header-actions">
          <button className="crm-button ghost" onClick={loadThoughts} disabled={loading}>
            {loading ? 'Syncing…' : 'Refresh'}
          </button>
          <button className="crm-button ghost crm-theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            {theme === 'dark' ? <Sun weight="fill" size={18} /> : <Moon weight="fill" size={18} />}
          </button>
          {user && (
            <button className="crm-button ghost crm-user-btn" onClick={handleSignOut}>
              {user.picture && <img src={user.picture} alt="" className="crm-user-avatar" />}
              <span className="crm-user-name">{user.name?.split(' ')[0] || 'Sign out'}</span>
            </button>
          )}
        </div>
      </header>

      <main className="crm-main crm-main-full">
        {error && <div className="crm-error" style={{ marginBottom: 24 }}>{error}</div>}

        <div className="crm-list-header">
          <div>
            <h2 className="crm-section-title">Thoughts</h2>
            <p className="crm-section-subtitle">{filteredThoughts.length} thoughts</p>
          </div>
          <div className="crm-filters">
            <select
              className="crm-filter-select"
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
            >
              <option value="all">All priorities</option>
              {priorityOptions.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>

        {loading && thoughts.length === 0 ? (
          <div className="crm-todos-loading"><div className="crm-spinner" /><p>Loading thoughts...</p></div>
        ) : filteredThoughts.length === 0 ? (
          <div className="crm-empty-state">
            <div className="crm-empty-icon"><LightbulbFilament weight="fill" size={32} /></div>
            <p className="crm-empty-title">No thoughts yet</p>
            <p className="crm-empty-desc">Add a thought from a client detail page to see it here.</p>
          </div>
        ) : (
          <div className="crm-thought-grid crm-thought-grid-full">
            {filteredThoughts.map(thought => (
              <button
                key={thought.id}
                className="crm-thought-card"
                data-priority={thought.priority || 'casual'}
                data-size={getThoughtSize(thought.text)}
                onClick={() => navigate(`/dashboard/${thought.client_id}`)}
              >
                <div className="crm-thought-card-top">
                  <span className="crm-thought-priority">{getPriorityLabel(thought.priority)}</span>
                </div>
                <p className="crm-thought-text">{thought.text}</p>
                <div className="crm-thought-meta">
                  <span className="crm-thought-date">
                    {formatThoughtDate(thought.date_thought || thought.created_at)}
                  </span>
                  <span className="crm-thought-client-name">{thought.company || thought.name || 'Client'}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Thoughts;
